import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function About() {
  return (
    <>
      <Helmet>
        <title>RentFi | About Our Real Estate Investment Platform</title>
        <meta 
          name="description" 
          content="Learn about RentFi's mission to democratize real estate investment through blockchain technology. Discover how we're making property investment accessible, transparent, and profitable for everyone." 
        />
        <meta 
          name="keywords" 
          content="RentFi about, blockchain real estate, property investment platform, real estate tokenization, investment democratization" 
        />
        <link rel="canonical" href="https://rentfi.io/about" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-16">
              <H1 className="text-primary mb-4">
                Bridging Traditional Real Estate & Blockchain Technology
              </H1>
              <Body className="text-gray-600 max-w-3xl mx-auto">
                Making real estate investment accessible, transparent, and efficient
              </Body>
            </div>

            <div className="text-center mb-16">
              <img 
                src="https://rentfi.b-cdn.net/rentfi-logo.webp"
                alt="RentFi - Revolutionizing Real Estate Investment Through Blockchain Technology"
                className="h-32 mx-auto"
              />
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <H2 className="text-primary mb-4">Our Mission</H2>
                  <Body className="text-gray-600">
                    To democratize real estate investment through blockchain technology, 
                    providing accessible, transparent, and profitable property investment 
                    opportunities to investors worldwide.
                  </Body>
                </div>
                <div>
                  <H2 className="text-primary mb-4">Our Vision</H2>
                  <Body className="text-gray-600">
                    To become the leading decentralized real estate investment platform, 
                    setting new standards for transparency and efficiency in property 
                    investment.
                  </Body>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <Body className="text-gray-600">
                While real estate ownership comes with complexity and high barriers to entry, 
                RentFi focuses on what matters most to investors: reliable monthly income. 
                By leveraging blockchain technology and professional property management, 
                we distribute stable yields from one of the world's most resilient asset 
                classes - real estate. Through market ups and downs, property rental income 
                has remained one of the most consistent forms of passive returns.
              </Body>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">
                The Real Estate Revolution
              </H2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gray-50 p-6 rounded-lg text-center">
                  <H3 className="text-gray-900 mb-2">Current Market Size</H3>
                  <p className="text-primary mb-2 font-kanit text-[32px] leading-[40px]">$317T</p>
                  <Body className="text-gray-600">Global Real Estate Market</Body>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg text-center">
                  <H3 className="text-gray-900 mb-2">2030 Projection</H3>
                  <p className="text-primary mb-2 font-kanit text-[32px] leading-[40px]">$16.1T</p>
                  <Body className="text-gray-600">Total RWA on-chain</Body>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg text-center">
                  <H3 className="text-gray-900 mb-2">Currently on-chain</H3>
                  <p className="text-primary mb-2 font-kanit text-[32px] leading-[40px]">&lt;0.1%</p>
                  <Body className="text-gray-600">of total market</Body>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Strategic Partners</H2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <img 
                    src="https://rentfi.b-cdn.net/rentfi-kramer-levin.webp"
                    alt="Kramer Levin - Leading International Law Firm and Legal Partner"
                    className="h-12 mb-4"
                  />
                  <H3 className="text-gray-900 mb-4">Legal Partner</H3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Leading international law firm</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Digital assets expertise</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Securities law specialists</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Regulatory compliance focus</Body>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <img 
                    src="https://rentfi.b-cdn.net/rentfi-block-consulting.webp"
                    alt="Block Consulting - Strategic Marketing and Blockchain Communication Partner"
                    className="h-12 mb-4"
                  />
                  <H3 className="text-gray-900 mb-4">Marketing Partner</H3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Strategic marketing experts</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Blockchain specialization</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Digital asset communication</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Market positioning</Body>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <H2 className="text-primary mb-8 text-center">Our Approach</H2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Innovation</H3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Blockchain technology integration</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Smart contract automation</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Efficient distribution system</Body>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Security</H3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Professional property management</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Regulatory compliance</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Audited smart contracts</Body>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Transparency</H3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Real-time reporting</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Clear documentation</Body>
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-secondary rounded-full mr-2"></span>
                      <Body className="text-gray-600">Regular updates</Body>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}